import ReactPDF, { View } from '@react-pdf/renderer';
import React from 'react';
import Dialog from 'src/components/common/Dialog';
import DialogList from 'src/dialogs';
import { Page, Text, Document, StyleSheet } from '@react-pdf/renderer';
import { AnyObject } from 'final-form';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import LeaseClause from 'src/models/LeaseClause';
import MathUtil from 'src/util/MathUtil';
import Acknowledgement from 'src/models/leasing/Acknowledgement';
import SignatureBlock from 'src/models/leasing/SignatureBlock';
import LeaseClauseType from 'src/models/LeaseClauseType';
import LocationModel from 'src/models/leasing/LocationModel';
import moment from 'moment';
import ContractAddendumClause from './redesign/ContractGenerationModels/ContractAddendumClause';
import Lease from 'src/models/leasehold/Lease';
// tslint:disable: max-line-length

const styles = StyleSheet.create({
    pageMain: {
        backgroundColor: '#FFFFFF',
        paddingTop: 100,
        paddingBottom: 70,
        paddingHorizontal: 80,
    },
    page: {
        backgroundColor: '#FFFFFF',
        paddingTop: 35,
        paddingBottom: 65,
        paddingHorizontal: 80,
    },
    pageNumbers: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10,
        color: '#8e9094',
    },
    section: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
    },
    bold: {
        fontFamily: 'Times-Bold',
    },
    fontNormal: {
        fontFamily: 'Times-Roman',
        fontWeight: 'normal',
        fontSize: 9,
    },
    mt1: {
        marginTop: 10,
    },
    mt2: {
        marginTop: 20,
    },
    mt3: {
        marginTop: 30,
    },
    mt4: {
        marginTop: 40,
    },
    mb2: {
        marginBottom: 20,
    },
    mr1: {
        marginRight: 5,
    },
    mr2: {
        marginRight: 20,
    },
    ml1: {
        marginLeft: 5,
    },
    ml2: {
        marginLeft: 20,
    },
    center: {
        textAlign: 'center',
    },
    text: {
        textAlign: 'justify',
    },

    underlined: {
        textDecoration: 'underline',
    },
    mailingInfo: {
        fontSize: 10,
        fontFamily: 'Times-Roman',
    },
    exhibitHeading: {
        textAlign: 'center',
        fontSize: 24,
        fontWeight: 'bold',
    },
    acknowledgementHeading: {
        textAlign: 'center',
        fontSize: '18',
        fontWeight: 'bold',
    },
    leaseHeading: {
        textAlign: 'center',
        fontSize: 20,
        fontFamily: 'Times-Bold',
    },
    doubleLines: {
        borderTop: '1px solid black',
        borderBottom: '1px solid black',
    },
    lineHeight: {
        lineHeight: 1.25,
    },
    h2: {
        fontSize: 12,
    },
    h3: {
        fontSize: 10,
    },
    lessorLabel: {
        width: '75px',
        fontSize: 9,
        textAlign: 'right',
        marginRight: '4',
        fontWeight: 'bold',
    },
    lessorData: {
        maxWidth: '20%',
        fontSize: 9,
    },
    footerText: {
        color: 'grey',
        fontSize: 9,
    },
    ownershipColumn: {
        width: '14%',
        fontSize: 9,
        textAlign: 'right',
    },
    bonusColumn: {
        width: '14%',
        fontSize: 9,
        textAlign: 'right',
        paddingRight: 15,
    },
    dudleyInfo: {
        position: 'absolute',
        bottom: '50px',
        left: '70px',
    },
    sbTitle: {
        textAlign: 'center',
        marginBottom: 10,
    },
});

interface IMemoDialogProps {
    hideDialog?: () => void;
    // TODO: Update IContract to include these missing fields.
    leaseData?: Lease;
    leaseClauses?: ContractAddendumClause[];
    leaseClauseTypes?: LeaseClauseType[];
    location?: LocationModel;
}

const MemoDialog = (props: IMemoDialogProps) => {
    const dispatch = useDispatch();
    // const firstIncludedTract = props?.leaseData?.tracts?.find((tract: AnyObject) => tract.includeTract === true);
    const totalGrossAcres = props?.leaseData?.tracts?.reduce((acc: number, tract: AnyObject) => {
        if (tract.includeTract === true) {
            let tempGrossAcres = parseFloat(tract.grossAcres) || 0;
            return acc + tempGrossAcres;
        } else {
            return acc;
        }
    }, 0);

    const leaseDescription = () => {
        const legalDescription: string = !!props?.leaseData ? props?.leaseData?.legalDescription : '';

        if (!legalDescription) {
            return <Text style={{ fontSize: 9 }}>No Legal Description Available</Text>;
        }

        if (legalDescription.length >= 1000) {
            return (
                <Text style={{ fontSize: 10, marginTop: '10px', textAlign: 'center' }}>
                    "SEE EXIBIT "A" ATTACHED HERETO AND MADE A PART HEREOF FOR LEGAL DESCRIPTION"
                </Text>
            );
        } else {
            return (
                <>
                    <Text style={{ textAlign: 'center', fontSize: 10, fontWeight: 'bold', marginBottom: 7, marginTop: 7 }}>LEGAL DESCRIPTION:</Text>
                    <Text style={{ fontSize: 10 }}>{legalDescription}</Text>
                </>
            );
        }
    };

    const termValDisplay = (mode: string): string => {
        if (mode === 'normal') {
            let primaryTerm: number = !!props?.leaseData ? parseInt(props?.leaseData?.leasehold?.primaryTerm + '', 10) : undefined;
            let primaryTermUnits: string = !!props?.leaseData ? props?.leaseData?.leasehold?.primaryTermUnits : undefined;

            if (!primaryTerm && !primaryTermUnits) {
                return '';
            }

            return `${MathUtil.numberToEnglish(primaryTerm, ' ').toUpperCase()} (${primaryTerm}) ${primaryTermUnits.toUpperCase()}`;
        }

        if (mode === 'option') {
            let optionTerm: number = !!props?.leaseData ? parseInt(props?.leaseData?.leasehold?.optionTerm + '', 10) : undefined;
            let optionTermUnits: string = !!props?.leaseData ? props?.leaseData?.leasehold?.optionTermUnits : undefined;
            if (!optionTerm && !optionTermUnits) {
                return '';
            }

            return `${MathUtil.numberToEnglish(optionTerm)} (${optionTerm}) ${optionTermUnits.toUpperCase()}`;
        }

        return '';
    };

    const optionToExtend = () => {
        if (!!props?.leaseData?.leasehold?.optionExercised) {
            return `, with an option to extend for an additional ${termValDisplay('option')}`;
        } else {
            return '';
        }
    };

    const lesseeAddress = () => {
        return !!props?.leaseData
            ? `${props?.leaseData?.lesseeAddressLine1}, ${props?.leaseData?.lesseeCity}, ${props?.leaseData?.lesseeState}, ${props?.leaseData?.lesseeZipCode}`
            : '';
    };

    const displaySignatureBlocks = () => {
        return !!props?.leaseData
            ? props?.leaseData?.acknowledgements?.map((la: Acknowledgement) => {
                  return (
                      la.signatureBlocks.length > 0 &&
                      la.signatureBlocks.map((sb: SignatureBlock, index: number) => {
                          return (
                              <View key={index} wrap={false}>
                                  <Text style={[styles.fontNormal, styles.mt3, styles.text, styles.bold]}>{!!sb.title && sb?.title}</Text>
                                  <Text style={[styles.fontNormal, styles.mt2, styles.text]}>Lessor:</Text>
                                  <Text style={[styles.fontNormal, styles.mt2, styles.text, styles.bold]}>____________________________________</Text>
                                  <Text style={[styles.fontNormal, styles.mt1, styles.text]}>{sb.ownerName}</Text>
                              </View>
                          );
                      })
                  );
              })
            : [];
    };

    const displayAcknowledgements = () => {
        return !!props?.leaseData
            ? props?.leaseData?.acknowledgements?.map((la: Acknowledgement, index: number) => {
                  return (
                      <View key={index} wrap={false}>
                          <Text style={[styles.fontNormal, styles.mt3, styles.text]}>{la.text}</Text>
                      </View>
                  );
              })
            : [];
    };

    const additionalProvisions = () => {
        let additionalProvisionNumber: number = 0;
        let renderClauses: ContractAddendumClause[] = [];
        let leaseClauses: ContractAddendumClause[] = !!props?.leaseData && props?.leaseData?.contractClauses;

        leaseClauses?.forEach((llc: AnyObject, index: number) => {
            let clause: ContractAddendumClause = !!props?.leaseClauses
                ? props?.leaseClauses.find((lc) => lc.id === llc.contractAddendumClauseId)
                : undefined;

            if (!!clause) {
                renderClauses.push(clause);
            }
        });

        return renderClauses?.map((rc: ContractAddendumClause, index: number) => {
            additionalProvisionNumber++;
            return (
                <Text key={index} style={[styles.fontNormal, styles.mt2, styles.text]}>
                    {additionalProvisionNumber}) {`${rc.leaseClauseType}: ${rc.clauseText}`}
                </Text>
            );
        });
    };

    const adhocProvisions = () => {
        let additionalProvisionNumber: number = 0;

        return (
            !!props?.leaseData &&
            props?.leaseData?.adHocClauses?.map((lc: LeaseClause, index: number) => {
                let type: string = !!props?.leaseClauseTypes ? props?.leaseClauseTypes?.find((lct) => lct.id === lc.leaseClauseTypeId)?.name : '';

                additionalProvisionNumber++;
                return (
                    <Text key={index} style={[styles.fontNormal, styles.mt2, styles.text]}>
                        {additionalProvisionNumber + ')'} {`${type}: ${lc.clauseText}`}
                    </Text>
                );
            })
        );
    };

    const renderSignaturePage = () => {
        return !props?.leaseData ? (
            <></>
        ) : props?.leaseData?.acknowledgements?.length > 0 ? (
            <Page size="LEGAL" style={styles.page}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        rowGap: '5rem',
                    }}
                >
                    <Text style={[styles.fontNormal, styles.mt3, styles.sbTitle, styles.h2, styles.bold]}>
                        IN TESTIMONY WHEREOF, I sign the ____ day of ____________________, 20____.
                    </Text>
                    {displaySignatureBlocks()}
                </div>
                <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
            </Page>
        ) : (
            <></>
        );
    };

    const renderAcknowledgementPage = () => {
        return !props?.leaseData ? (
            <></>
        ) : props?.leaseData?.acknowledgements?.length > 0 ? (
            <Page size="LEGAL" style={styles.page}>
                <Text style={[styles.acknowledgementHeading, styles.mt3]}>(ACKNOWLEDGEMENT)</Text>
                {displayAcknowledgements()}
                <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
            </Page>
        ) : (
            <></>
        );
    };

    const legalToExhibit = () => {
        return !props?.leaseData ? (
            <></>
        ) : props?.leaseData?.legalDescription.length > 1000 ? (
            <Page size="LEGAL" style={styles.page}>
                <Text style={styles.exhibitHeading}>Exhibit A</Text>
                <Text style={[styles.fontNormal, styles.mt1, styles.text]}>{props?.leaseData?.legalDescription}</Text>
                <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
            </Page>
        ) : (
            <></>
        );
    };

    const displayExhibits = () => {
        return !props?.leaseData ? (
            <></>
        ) : props?.leaseData?.adHocClauses.length > 0 ? (
            <Page size="LEGAL" style={styles.page}>
                <Text style={styles.exhibitHeading}>Exhibit {props?.leaseData?.legalDescription.length > 1000 ? 'B' : 'A'}</Text>
                <Text style={[styles.fontNormal, styles.mt3, styles.h2]}>Additional Provisions</Text>
                {adhocProvisions()}
                {additionalProvisions()}
                <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
            </Page>
        ) : (
            <></>
        );
    };

    return (
        <Dialog title="MEMO" dialogName={DialogList.MEMO_VIEWER} onCloseDialog={() => dispatch(actions.layout.hideDialog())}>
            <ReactPDF.PDFViewer style={{ height: '85vh', width: '70vw' }}>
                <Document>
                    <Page size="LEGAL" style={styles.pageMain}>
                        <View fixed={true}>
                            <View>
                                <Text style={[styles.bold, styles.h3]}>
                                    Note of Confidentiality Rights: If you are a natural person, you may remove or strike any of the following
                                    information from this instrument before it is filed in the public records: Your social security number or your
                                    driver's license number.
                                </Text>
                            </View>

                            <View style={{ marginTop: '15px' }}>
                                <Text
                                    style={{
                                        fontSize: 11,
                                        textDecoration: 'underline',
                                        textDecorationStyle: 'solid',
                                        textAlign: 'center',
                                        fontFamily: 'Times-Bold',
                                    }}
                                >
                                    MEMORANDUM OF OIL, GAS & MINERAL LEASE
                                </Text>
                            </View>

                            <View
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    marginTop: '10px',
                                }}
                            >
                                <View>
                                    <Text style={{ fontSize: 9, fontFamily: 'Times-Bold' }}>
                                        STATE OF {!!props?.location ? props?.location?.state?.toUpperCase() : '______'}
                                    </Text>
                                    <Text style={{ fontSize: 9, marginTop: '10px', fontFamily: 'Times-Bold' }}>
                                        COUNTY OF {!!props?.location ? props?.location?.counties[0]?.toUpperCase() : '___________'}
                                    </Text>
                                </View>
                                <View style={{ marginLeft: '30px' }}>
                                    <Text style={{ fontSize: 9 }}>§</Text>
                                    <Text style={{ fontSize: 9 }}>§</Text>
                                    <Text style={{ fontSize: 9 }}>§</Text>
                                </View>
                            </View>
                        </View>

                        <View style={{ marginTop: '15px' }}>
                            <Text style={[{ fontSize: 10 }, styles.lineHeight]}>
                                THAT {props?.leaseData?.ownersOnDocument}, herein dealing with the sole and seprate propety, whose address is{' '}
                                {props?.leaseData?.ownersAddressOnDocument}, {'(hereinafter referred to as "Lessor")'} and{' '}
                                {props?.leaseData?.lesseeName}, whose address is {lesseeAddress()} {'(hereinafter referred to as "Lessee")'}, hereby
                                acknowledge and give notice that the Lessor has executed and delivered to Lessee an Oil, Gas and Mineral Lease dated{' '}
                                {moment(props?.leaseData?.effectiveDate).format('MMMM Do YYYY')}, covering that certain {totalGrossAcres} acres of
                                land, more or less, in {!!props?.leaseData?.lesseeCity ? props?.leaseData?.lesseeCity : ''},{' '}
                                {!!props?.leaseData?.lesseeState ? props?.leaseData?.lesseeState : ''}, {'(the "Lease")'} and said land being further
                                described as follows:
                            </Text>
                        </View>

                        <View style={{ marginBottom: '10px', marginTop: '10px' }}>{leaseDescription()}</View>

                        <View>
                            <Text style={[{ fontSize: 10, marginTop: '10px' }, styles.lineHeight]}>
                                The Lease provides for a primary term of{termValDisplay('normal')} from the effective date of the Lease{''}
                                {optionToExtend()}, and is subject to all terms, conditions and provisions set forth in the Lease, to which Lease
                                reference is herein made for all purposes. Lessee shall retain possession of the fully executed original Lease and the
                                Lessor with a copy of said Lease, which is open for examination and investigation by any party of interest during
                                reasonable business hours in the office of Lessee.
                            </Text>
                        </View>

                        <View style={{ marginTop: '10px' }}>
                            <Text style={[{ fontSize: 10 }, styles.lineHeight]}>
                                THIS Memorandum of Oil, Gas and Mineral Lease, is executed by Lessor and placed of record to evidence the existence of
                                the Lease. The Memorandum is recorded in lieu of the Lease in the Official Public Records and for the purpose of
                                placing all person on notice of the existence of the Lease.
                            </Text>
                        </View>

                        <Text
                            style={
                                props?.leaseData?.legalDescription?.length >= 500
                                    ? { fontSize: 10, marginTop: '30px', textAlign: 'center' }
                                    : { fontSize: 10, marginTop: '150px', textAlign: 'center' }
                            }
                        >
                            [ SIGNATURES ON THE FOLLOWING PAGE ]
                        </Text>

                        <View style={styles.dudleyInfo} fixed={true}>
                            <View>
                                <Text style={[styles.bold, styles.h3]}>After Recording, please return to:</Text>
                                <Text style={{ fontSize: 10 }}>Dudley Land Company</Text>
                                <Text style={{ fontSize: 10 }}>5925 North Robinson Avenue</Text>
                                <Text style={{ fontSize: 10 }}>
                                    Oklahoma City OK, 73118 {'      '} TSDI LPR#{props?.leaseData?.clientDocNumber}
                                </Text>
                            </View>
                        </View>

                        <Text style={styles.pageNumbers} render={({ pageNumber }) => `${pageNumber}`} fixed={true} />
                    </Page>

                    {/* Functions to render pages based on if there are items to render or other logic */}
                    {renderSignaturePage()}
                    {renderAcknowledgementPage()}
                    {legalToExhibit()}
                    {displayExhibits()}
                </Document>
            </ReactPDF.PDFViewer>
        </Dialog>
    );
};

export default MemoDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/MemoDialog.tsx